<script lang="ts">
  /**
   * Where the community ranks for each keyword, and the top ten above it — filling in
   * row by row as the audit streams.
   */
  import { discoveryUrl, formatPrice, volumeColor, volumeLabel, volumeWidth } from '../../audit';
  import type { AuditRun } from '../../audit/run.svelte';
  import LeaderLogo from './LeaderLogo.svelte';
  import { keepOnScreen } from './keepOnScreen';

  let { run }: { run: AuditRun } = $props();

  function members(n: number): string {
    return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n);
  }
</script>

{#if run.plan.length === 0}
  <!-- Between submit and the plan frame the keywords are not known yet, so
       there is nothing to draw — not even a skeleton row. An uncached community
       spends a second or two here, and an empty table with headers reads as a
       result rather than a wait. -->
  <div class="flex flex-col items-center gap-3 py-20 text-base-content/60">
    <span class="loading loading-spinner loading-lg"></span>
    Reading your keywords…
  </div>
{:else}
  {#if run.done < run.plan.length}
    <!-- Only while it is loading. Left up at 100% it is a solid line that means
         nothing and costs a row of vertical space on every screen after. -->
    <progress
      class="progress progress-primary mb-4 h-1 w-full"
      value={run.done}
      max={run.plan.length}
    ></progress>
  {/if}
{/if}

<!-- No overflow wrapper: the competitor hover-cards are absolutely positioned
     and a scroll container would clip them. Instead the table stacks into cards
     whenever its own box is too narrow for the columns (`.audit-table`). -->
<div class="audit-table rounded-box border border-base-300 bg-base-100">
  <table class="table table-sm sm:table-md stack-sm">
    <thead>
      <tr>
        <th>Keyword</th>
        <th class="text-right">Rank</th>
        <th class="w-40 text-right">Search Results</th>
        <th>Top 10 for this keyword</th>
      </tr>
    </thead>
    <tbody>
      {#each run.orderedPlan as keyword (keyword)}
        {@const row = run.rows[keyword]}
        <tr class={row ? '' : 'opacity-40'}>
          <td class="font-medium" data-cell="kw">
            <!-- Opens Skool's own search for this keyword, so any number on the
                 row can be checked against the page it came from. Written flush
                 against the keyword: a space is a line-break opportunity, and the
                 arrow would wrap onto a line of its own. -->
            {keyword}<a
              class="ml-1 text-base-content/30 transition-colors hover:text-primary"
              href={discoveryUrl(keyword, run.community?.language)}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open Skool search for "${keyword}"`}
              aria-label={`Open Skool search for ${keyword}`}>&rarr;</a
            >
          </td>
          <td class="text-right tabular-nums" data-cell="rank">
            {#if !row}
              <span class="loading loading-dots loading-xs"></span>
            {:else if row.rank === null}
              <span class="text-base-content/50">unranked</span>
            {:else}
              <span class="font-semibold">#{row.rank}</span>
            {/if}
          </td>
          <td class="text-right" data-cell="size">
            {#if row}
              <div
                class="flex cursor-pointer flex-col items-end gap-1"
                title={volumeLabel(row.n_hits)}
              >
                <span class="tabular-nums">{row.n_hits.toLocaleString()}</span>
                <div class="h-1.5 w-full rounded-full bg-base-300">
                  <div
                    class="h-full rounded-full {volumeColor(row.n_hits)}"
                    style="width: {volumeWidth(row.n_hits)}%"
                  ></div>
                </div>
              </div>
            {:else}
              <span class="text-base-content/40">—</span>
            {/if}
          </td>
          <td data-cell="logos">
            {#if row}
              <div class="flex flex-wrap items-center gap-1.5 sm:gap-2">
                {#each row.leaders as l, i (l.rank)}
                  <!-- Ten logos overflow a phone. Five is what fits, and the
                       rank badges keep the order legible either way. -->
                  <div
                    class="group relative shrink-0"
                    class:hidden={i >= 5}
                    class:sm:block={i >= 5}
                  >
                    <a
                      href={l.slug ? `https://www.skool.com/${l.slug}` : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="block cursor-pointer"
                    >
                      <LeaderLogo leader={l} />
                    </a>
                    <!-- Hover card. `pointer-events-none` so it never eats the
                         click on the avatar underneath it.

                         SHOWN AND HIDDEN, never faded. A fade means the card
                         spends its whole transition semi-transparent, with the
                         neighbouring logos legible straight through the text —
                         and it starts every hover unreadable. -->
                    <div
                      class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2
                             hovercard hidden w-48 -translate-x-1/2 rounded-box p-3
                             shadow-lg group-hover:block"
                      use:keepOnScreen
                    >
                      <!-- privacy-ok: public community name, not a member -->
                      <p class="text-sm font-semibold leading-tight">
                        {l.name}{l.is_you ? ' (you)' : ''}
                      </p>
                      <p class="mt-1 text-xs opacity-80">
                        #{l.rank}
                        <span class="mx-1 opacity-50">·</span>
                        {members(l.total_members)} members
                        <span class="mx-1 opacity-50">·</span>
                        <!-- privacy-ok: a public community's own listed price -->
                        {formatPrice(l.price_amount, l.price_currency, l.price_interval)}
                      </p>
                    </div>
                  </div>
                {/each}
              </div>
            {:else}
              <div class="flex flex-wrap gap-2">
                {#each [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as i (i)}
                  <div
                    class="h-7 w-7 shrink-0 rounded-full bg-base-300 sm:h-9 sm:w-9"
                    class:hidden={i >= 5}
                    class:sm:block={i >= 5}
                  ></div>
                {/each}
              </div>
            {/if}
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>
